import React, { memo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Card, Divider, Empty, List } from "antd";
import { useInfiniteQuery } from "@tanstack/react-query";
import ActivityService from "@Services/ActivityService";
import { ProfileActivitySkeleton } from "@Components/activity/ProfileActivitySkeleton";
import ActivityItem from "./ActivityItem";
import { QueryKeys, ThreeMinutes } from "@Constants";
export interface IGridLayout {
    gutter: number;
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
}

interface IProps {
    userIds: string[];
    queryKey: string;
    gridLayout?: IGridLayout;
    onActivityClick?: (activityInstanceId: string) => void;
    style?: React.CSSProperties;
}

const defaultLayout: IGridLayout = { gutter: 20, xs: 1, sm: 1, md: 1, lg: 1, xl: 2, xxl: 2 };

const ActivityList: React.FC<IProps> = ({ userIds, queryKey, gridLayout, style = {}, onActivityClick }: IProps) => {
    if (userIds?.length == 0) return null;

    /**
     * Get the activities using the parameters provided
     * @param pageParam
     */
    const getActivities = async ({ pageParam = 0 }) => {
        const isPublic = queryKey === QueryKeys.ActivityListPublic;
        const activityService = new ActivityService();
        const result = await activityService.getUser(userIds, [], [], "", "", pageParam, isPublic);
        return result.hasErrors ? null : result.value;
    };

    const query = useInfiniteQuery([queryKey], getActivities, {
        getNextPageParam: (payload) => (payload?.nextPage === -1 ? null : payload?.nextPage),
        refetchOnWindowFocus: false,
        staleTime: ThreeMinutes,
    });

    const dataLength = query.data?.pages?.reduce((c, x) => c + (x?.activities?.length ?? 0), 0) ?? 0;

    return query.status === "loading" ? (
        <List
            className="activity-list"
            itemLayout="vertical"
            grid={gridLayout ?? defaultLayout}
            dataSource={[...Array(6).keys()]}
            renderItem={() => (
                <List.Item>
                    <ProfileActivitySkeleton />
                </List.Item>
            )}
            style={style}
        />
    ) : query.data && dataLength > 0 ? (
        <InfiniteScroll
            dataLength={dataLength}
            next={query.fetchNextPage}
            hasMore={query.hasNextPage}
            loader={
                <List
                    className="activity-list"
                    itemLayout="vertical"
                    grid={gridLayout ?? defaultLayout}
                    dataSource={[...Array(2).keys()]}
                    renderItem={() => (
                        <List.Item>
                            <ProfileActivitySkeleton />
                        </List.Item>
                    )}
                />
            }
            endMessage={<Divider plain />}
            scrollableTarget="content-wrapper"
        >
            <List
                className="activity-list"
                itemLayout="vertical"
                grid={gridLayout ?? defaultLayout}
                dataSource={query.data?.pages}
                renderItem={(group) =>
                    group?.activities?.map((activity) => (
                        <List.Item>
                            <ActivityItem
                                activity={activity}
                                onClick={onActivityClick}
                                key={"profile-activity-item-" + activity?.activityInstanceId}
                            />
                        </List.Item>
                    ))
                }
                style={style}
            />
        </InfiniteScroll>
    ) : (
        <Card title="Activities" className="neumorphic-subtle activities-search-list">
            <Empty
                style={{ paddingBottom: "40px" }}
                description={"No results to display."}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
        </Card>
    );
};

export default memo(ActivityList);
